import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const ROICalculator = () => {
  const [formData, setFormData] = useState({
    employees: '',
    service: 'hr',
    duration: '12'
  });

  const [result, setResult] = useState(null);

  const serviceOptions = [
    { value: 'hr', label: 'HR Solutions' },
    { value: 'recruitment', label: 'Recruitment Services' },
    { value: 'trade', label: 'Global Trade' },
    { value: 'engineering', label: 'Engineering Services' }
  ];

  const durationOptions = [
    { value: '6', label: '6 Months' },
    { value: '12', label: '12 Months' },
    { value: '24', label: '24 Months' },
    { value: '36', label: '36 Months' }
  ];

  const calculateROI = () => {
    const employees = parseInt(formData?.employees) || 0;
    const duration = parseInt(formData?.duration) || 12;

    const baseROI = {
      hr: { savings: 2500, efficiency: 35, satisfaction: 40 },
      recruitment: { savings: 3000, efficiency: 45, satisfaction: 35 },
      trade: { savings: 5000, efficiency: 50, satisfaction: 45 },
      engineering: { savings: 4000, efficiency: 40, satisfaction: 38 }
    };

    const service = baseROI?.[formData?.service];
    const totalSavings = (service?.savings * employees * duration) / 12;
    const efficiencyGain = service?.efficiency;
    const satisfactionIncrease = service?.satisfaction;

    setResult({
      totalSavings: totalSavings?.toLocaleString('en-IN'),
      efficiencyGain,
      satisfactionIncrease,
      paybackPeriod: Math.ceil(duration / 3)
    });
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Calculator" size={24} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground">ROI Calculator</h3>
          <p className="text-sm text-muted-foreground">Estimate your potential benefits</p>
        </div>
      </div>
      <div className="space-y-4 mb-6">
        <Input
          type="number"
          label="Number of Employees"
          placeholder="Enter employee count"
          value={formData?.employees}
          onChange={(e) => setFormData({ ...formData, employees: e?.target?.value })}
        />

        <Select
          label="Service Type"
          options={serviceOptions}
          value={formData?.service}
          onChange={(value) => setFormData({ ...formData, service: value })}
        />

        <Select
          label="Contract Duration"
          options={durationOptions}
          value={formData?.duration}
          onChange={(value) => setFormData({ ...formData, duration: value })}
        />

        <Button
          variant="default"
          fullWidth
          iconName="TrendingUp"
          iconPosition="left"
          onClick={calculateROI}
          disabled={!formData?.employees}
        >
          Calculate ROI
        </Button>
      </div>
      {result && (
        <div className="space-y-3 pt-6 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3">Estimated Benefits</h4>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-success/10 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="DollarSign" size={16} className="text-success" />
                <span className="text-xs text-muted-foreground">Cost Savings</span>
              </div>
              <p className="text-lg md:text-xl font-bold text-foreground">₹{result?.totalSavings}</p>
            </div>

            <div className="bg-primary/10 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="TrendingUp" size={16} className="text-primary" />
                <span className="text-xs text-muted-foreground">Efficiency Gain</span>
              </div>
              <p className="text-lg md:text-xl font-bold text-foreground">{result?.efficiencyGain}%</p>
            </div>

            <div className="bg-accent/10 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Heart" size={16} className="text-accent" />
                <span className="text-xs text-muted-foreground">Satisfaction</span>
              </div>
              <p className="text-lg md:text-xl font-bold text-foreground">{result?.satisfactionIncrease}%</p>
            </div>

            <div className="bg-secondary/10 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Clock" size={16} className="text-secondary" />
                <span className="text-xs text-muted-foreground">Payback Period</span>
              </div>
              <p className="text-lg md:text-xl font-bold text-foreground">{result?.paybackPeriod} months</p>
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-4">
            * Estimates based on industry averages and historical client data
          </p>
        </div>
      )}
    </div>
  );
};

export default ROICalculator;